import { motion, AnimatePresence } from "framer-motion";
import {
  Copy, RefreshCw, Scissors, Expand, RotateCcw,
  Sparkles, Save, Check, Loader2,
  Mic, ArrowRight, X, ThumbsUp, ThumbsDown,
  ChevronDown, ExternalLink, Share2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PostCoach } from "@/components/post-coach";
import { copyAsThread, hasThreadMarkers } from "@/lib/copy-for-platform";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";

const PLATFORM_LABELS: Record<string, string> = {
  twitter: "Twitter / X",
  linkedin: "LinkedIn",
  reddit: "Reddit",
  instagram: "Instagram",
  facebook: "Facebook",
};

interface VoiceAnalysis {
  sampleCount?: number;
  analysis?: Record<string, unknown> | null;
}

interface PostGeneratorResultPanelProps {
  result: { content: string; score: number; id: string } | null;
  isSingleLoading: boolean;
  drafts: { content: string; score: number; id: string; styleNote?: string }[] | null;
  undoStack: string[];
  savedToVault: boolean;
  draftContent: string;
  platform: string;
  copied: string | null;
  setCopied: (v: string | null) => void;
  handleCopy: (content: string, key?: string) => void;
  handleShare: (content: string, key?: string) => void;
  handleRefine: (action: string) => void;
  handleUndo: () => void;
  handleSaveToVault: () => void;
  createIdeaMutationPending: boolean;
  feedbackGiven: "up" | "down" | null;
  feedbackMessage: string | null;
  feedbackMutationPending: boolean;
  handlePostFeedback: (rating: "up" | "down") => void;
  coachTip: string | null;
  coachLoading: boolean;
  coachDismissed: boolean;
  setCoachDismissed: (v: boolean) => void;
  voiceSampleCount: number;
  voiceNudgeDismissed: boolean;
  setVoiceNudgeDismissed: (v: boolean) => void;
  voiceDnaOpen: boolean;
  setVoiceDnaOpen: (v: boolean) => void;
  voiceProfile: VoiceAnalysis | null | undefined;
  isTrimming: boolean;
}

export function PostGeneratorResultPanel({
  result,
  isSingleLoading,
  drafts,
  undoStack,
  savedToVault,
  draftContent,
  platform,
  copied,
  setCopied,
  handleCopy,
  handleShare,
  handleRefine,
  handleUndo,
  handleSaveToVault,
  createIdeaMutationPending,
  feedbackGiven,
  feedbackMessage,
  feedbackMutationPending,
  handlePostFeedback,
  coachTip,
  coachLoading,
  coachDismissed,
  setCoachDismissed,
  voiceSampleCount,
  voiceNudgeDismissed,
  setVoiceNudgeDismissed,
  voiceDnaOpen,
  setVoiceDnaOpen,
  voiceProfile,
}: PostGeneratorResultPanelProps) {
  const { toast } = useToast();
  return (
    <>
      <AnimatePresence>
        {result && !isSingleLoading && !drafts && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="pt-6 border-t border-border mt-6 space-y-2"
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Button variant="outline" size="sm" onClick={() => handleRefine("shorten")} className="rounded-lg h-10">
                <Scissors className="w-4 h-4 mr-2" /> Shorten
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleRefine("expand")} className="rounded-lg h-10">
                <Expand className="w-4 h-4 mr-2" /> Expand
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleRefine("polish")} className="rounded-lg h-10">
                <Sparkles className="w-4 h-4 mr-2 text-primary" /> Polish
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleRefine("regenerate")} className="rounded-lg h-10">
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
            </div>
            <AnimatePresence>
              {undoStack.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <Button
                    data-testid="undo-refine-btn"
                    variant="ghost"
                    size="sm"
                    onClick={handleUndo}
                    className="w-full rounded-lg h-9 text-muted-foreground hover:text-foreground border border-dashed border-border/60 gap-2"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    ← Undo refinement{undoStack.length > 1 ? ` (${undoStack.length} available)` : ""}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>

            {!savedToVault && (
              <div className="col-span-full mt-2 rounded-xl border border-primary/30 bg-primary/5 p-3">
                <p className="text-xs text-primary font-medium mb-2 flex items-center gap-1.5">
                  <Save className="w-3.5 h-3.5" /> Keep this post in your Idea Vault for later
                </p>
                <div className="flex gap-2">
                  <Button
                    className="flex-1 rounded-xl h-11 bg-primary hover:bg-primary/90"
                    onClick={handleSaveToVault}
                    disabled={createIdeaMutationPending}
                  >
                    {createIdeaMutationPending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save to Vault</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => draftContent && handleCopy(draftContent)} className="flex-1 rounded-xl h-11">
                    {copied === "main" ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copied === "main"
                      ? `Copied for ${PLATFORM_LABELS[platform] ?? platform}!`
                      : "Copy Post"}
                  </Button>
                </div>
              </div>
            )}
            {savedToVault && (
              <div className="col-span-full mt-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center justify-between">
                <p className="text-sm text-emerald-400 font-medium flex items-center gap-2">
                  <Check className="w-4 h-4" /> Saved to your Idea Vault
                </p>
                <Button variant="outline" size="sm" onClick={() => draftContent && handleCopy(draftContent)} className="rounded-lg">
                  {copied === "main" ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied === "main"
                    ? `Copied for ${PLATFORM_LABELS[platform] ?? platform}!`
                    : "Copy"}
                </Button>
              </div>
            )}

            <AnimatePresence>
              {platform === "twitter" && draftContent && hasThreadMarkers(draftContent) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <button
                    data-testid="copy-as-thread-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(copyAsThread(draftContent));
                      setCopied("thread");
                      setTimeout(() => setCopied(null), 2000);
                      toast({ title: "Thread copied!", description: "Each tweet is numbered and ready to paste." });
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 hover:bg-sky-500/20 text-sm font-medium transition-colors"
                  >
                    {copied === "thread" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied === "thread" ? "Thread copied!" : "Copy as thread (numbered)"}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="col-span-full flex gap-2">
              {platform === "twitter" && draftContent && (
                <button
                  data-testid="open-in-twitter-btn"
                  onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(draftContent)}`, "_blank")}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 hover:bg-sky-500/20 text-sm font-medium transition-colors"
                >
                  <ExternalLink className="w-4 h-4" /> Open in Twitter
                </button>
              )}
              {platform === "linkedin" && draftContent && (
                <button
                  data-testid="open-in-linkedin-btn"
                  onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&summary=${encodeURIComponent(draftContent)}`, "_blank")}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-600/10 border border-blue-600/20 text-blue-400 hover:bg-blue-600/20 text-sm font-medium transition-colors"
                >
                  <ExternalLink className="w-4 h-4" /> Share on LinkedIn
                </button>
              )}
              {draftContent && (
                <button
                  data-testid="share-post-btn"
                  onClick={() => handleShare(draftContent)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-secondary hover:bg-secondary/80 text-sm font-medium transition-colors"
                >
                  <Share2 className="w-4 h-4" /> Share
                </button>
              )}
            </div>

            <AnimatePresence>
              {!feedbackGiven && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="col-span-full flex items-center justify-center gap-3 pt-1"
                  data-testid="post-feedback-row"
                >
                  <span className="text-xs text-muted-foreground">Does this sound like you?</span>
                  <button
                    onClick={() => handlePostFeedback("up")}
                    data-testid="post-feedback-up"
                    disabled={feedbackMutationPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-muted-foreground border border-border hover:bg-green-500/10 hover:text-green-500 hover:border-green-500/30 transition-all"
                  >
                    <ThumbsUp className="w-3.5 h-3.5" /> Yes
                  </button>
                  <button
                    onClick={() => handlePostFeedback("down")}
                    data-testid="post-feedback-down"
                    disabled={feedbackMutationPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-muted-foreground border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-all"
                  >
                    <ThumbsDown className="w-3.5 h-3.5" /> Not quite
                  </button>
                </motion.div>
              )}
              {feedbackGiven && feedbackMessage && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="col-span-full text-center text-xs text-muted-foreground pt-1"
                  data-testid="post-feedback-message"
                >
                  {feedbackGiven === "up" ? "👍 " : "👎 "}{feedbackMessage}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {result && !isSingleLoading && !drafts && !coachDismissed && (coachLoading || coachTip) && (
          <PostCoach
            tip={coachTip}
            isLoading={coachLoading}
            className="mt-4"
            onDismiss={() => setCoachDismissed(true)}
          />
        )}
      </AnimatePresence>

      {result && !isSingleLoading && !drafts && voiceSampleCount >= 1 && voiceProfile?.analysis && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 rounded-2xl border border-primary/20 bg-primary/5 overflow-hidden"
          data-testid="voice-dna-panel"
        >
          <button
            type="button"
            onClick={() => setVoiceDnaOpen(!voiceDnaOpen)}
            className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-primary/5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-semibold text-primary">Voice DNA Applied</span>
            </div>
            <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${voiceDnaOpen ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence>
            {voiceDnaOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-3 pt-0 flex flex-wrap gap-1.5">
                  {(() => {
                    const a = voiceProfile.analysis as Record<string, unknown>;
                    const chips: { label: string; value: string; accent?: boolean }[] = [];
                    const hookStyle = typeof a.hookTaxonomy === "string" ? a.hookTaxonomy : null;
                    if (hookStyle) chips.push({ label: "Hook style", value: hookStyle, accent: true });
                    const sigs = Array.isArray(a.signaturePhrases) ? a.signaturePhrases as unknown[] : [];
                    if (sigs.length > 0) {
                      const phrase = typeof sigs[0] === "string" ? `"${sigs[0]}"` : null;
                      if (phrase) chips.push({ label: "Signature", value: phrase, accent: true });
                    }
                    if (a.tone && typeof a.tone === "string") chips.push({ label: "Tone", value: a.tone });
                    if (a.cadence && typeof a.cadence === "string") chips.push({ label: "Cadence", value: a.cadence });
                    if (a.vocabularyLevel && typeof a.vocabularyLevel === "string") chips.push({ label: "Vocab", value: a.vocabularyLevel });
                    const shown = chips.slice(0, 5);
                    if (shown.length === 0) return <p className="text-xs text-muted-foreground">Voice traits applied from your profile.</p>;
                    return shown.map((c, i) => (
                      <span
                        key={i}
                        className={`px-2.5 py-1 rounded-full text-xs border capitalize ${c.accent ? "bg-primary/10 text-primary border-primary/20" : "bg-secondary text-foreground border-border"}`}
                      >
                        {c.label}: {c.value}
                      </span>
                    ));
                  })()}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {result && !isSingleLoading && !drafts && voiceSampleCount < 3 && !voiceNudgeDismissed && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex items-start gap-3 px-4 py-3 rounded-xl border border-primary/25 bg-primary/5"
          data-testid="post-gen-voice-nudge"
        >
          <Mic className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">
              {voiceSampleCount === 0
                ? "Generated without your voice profile — it'll sound more like you once trained."
                : `${voiceSampleCount} of 3 samples added — a few more and the AI will sound just like you.`}
            </p>
            <div className="flex gap-4 mt-1.5">
              <Link
                href="/training"
                className="text-xs font-semibold text-primary hover:text-primary/80 flex items-center gap-1"
              >
                Add writing samples <ArrowRight className="w-3 h-3" />
              </Link>
              <Link
                href="/voice-style"
                className="text-xs font-semibold text-primary hover:text-primary/80 flex items-center gap-1"
              >
                Set preferences <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
          <button
            onClick={() => setVoiceNudgeDismissed(true)}
            className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-0.5"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      )}
    </>
  );
}
